
import {map, take, filter, timeout, catchError, tap} from 'rxjs/operators';
import {Injectable} from "@angular/core";
import {AsyncSubject, BehaviorSubject, Observable, of, ReplaySubject, Subject, Subscriber, Subscription, throwError} from "rxjs";
import {ArtifactId, ExpeditionId, BrowserSessionStateId, ProjectId, TestRunId} from '../../types/ids';
import {AuthService} from "../../services/auth.service";
import { HttpClient } from '@angular/common/http';
import {Artifact, ArtifactInfo, ArtifactType} from "../../types/artifact";
import {TestType} from "@webmate-web-core/shared/types/testmgmt";
import {DateTime} from "@webmate-web-core/shared/types/base-types";
import {APIResult, ApiSourcedPaginationRequest, ApiSourcedPaginationResult, QueryMetadata, queryMetadataToQueryParams} from '@webmate-web-core/shared/api/apibase.api';



/**
 * REST Access to webmate BrowserSession API.
 */
@Injectable({
  providedIn: "root"
})
export class ArtifactApi {

    static readonly ARTIFACT_TIMEOUT_MILLIS = 60000;
    static readonly ARTIFACT_REQUEST_BATCHINTERVAL = 500;
    static readonly MAX_ARTIFACTS_IN_ONE_REQUEST = 100;

    latestGetArtifactRequestBatch: ArtifactId[] = [];

    latestGetArtifactRequestBatchResponses = new Subject<Artifact>();

    constructor(public OAuth2AuthService: AuthService, private http: HttpClient) {
        let self = this;
        setTimeout(() => self.retrieveBatchedArtifacts(self), 1000);
    }

    getArtifact$(id: ArtifactId): Observable<Artifact> {
        this.latestGetArtifactRequestBatch.push(id);

        // only listen for the first artifact with the same id that you have requested
        return this.latestGetArtifactRequestBatchResponses.asObservable().pipe(
            filter(art => art.id == id),
            take(1),
            timeout(ArtifactApi.ARTIFACT_TIMEOUT_MILLIS),
            catchError(err => throwError(`Requesting artifact ${id} has timed out after ${ArtifactApi.ARTIFACT_TIMEOUT_MILLIS}`))
        );
    }

    getArtifactsInState$(id: BrowserSessionStateId): Observable<Artifact[]> {
        return this.http.get<Artifact[]>("/artifact/artifacts/" + id + "/artifacts-in-state");
    }

    getArtifacts$(ids: ArtifactId[]): Observable<Artifact[]> {
        return this.http.get<Artifact[]>("/artifact/artifacts?ids=" + ids.join(","));
    }

    getArtifactInfosInProjectPage$(
        projectId: ProjectId,
        optTestRunId: TestRunId | undefined,
        types?: Array<ArtifactType>,
        limit?: number,
        offset?: number
    ): Observable<ApiSourcedPaginationResult<ArtifactInfo>> {
        const queryMetadata = {filters: {}, limit, offset} as QueryMetadata;
        if (!!optTestRunId) {
            queryMetadata.filters.testRunId = optTestRunId;
        }
        if (!!types && types.length > 0) {
            queryMetadata.filters.types = types.join(",");
        }

        return new ApiSourcedPaginationRequest<ArtifactInfo>(`/projects/${projectId}/artifact-infos`, this.http, queryMetadataToQueryParams(queryMetadata))
            .fetch();
    }

    getArtifactsInProject$(projectId: ProjectId, optTestRunId: TestRunId | undefined, types?: Array<ArtifactType>): Observable<ArtifactInfo[]> {
        return this.getArtifactInfosInProjectPage$(projectId, optTestRunId, types).pipe(map(result => result.result));
    }

    getBrowserSessionArtifacts$(id: ExpeditionId, optLimit?: number, optStartTime?: DateTime): Observable<ArtifactInfo[]> {
        let queryParams = "";
        if (!!optLimit) {
            queryParams = queryParams + "limit=" + optLimit;
        }
        if (!!optStartTime) {
            queryParams = queryParams + "startTime=" + optStartTime;
        }
        return this.http.get<ArtifactInfo[]>(`/browsersession/${id}/artifacts` +
            (queryParams.length > 0 ? '?' + queryParams : ''));
    }

    retrieveBatchedArtifacts(self: ArtifactApi): void {
        if (self.latestGetArtifactRequestBatch.length > 0) {
            let requestsToBeRetrieved = self.latestGetArtifactRequestBatch.slice(0, ArtifactApi.MAX_ARTIFACTS_IN_ONE_REQUEST);
            self.latestGetArtifactRequestBatch = self.latestGetArtifactRequestBatch.slice(ArtifactApi.MAX_ARTIFACTS_IN_ONE_REQUEST);
            this.http.get<Artifact[]>("/artifact/artifacts?ids=" + requestsToBeRetrieved.join(",")).subscribe(result => {
                result.forEach(artifact => self.latestGetArtifactRequestBatchResponses.next(artifact));
            });
        }
        setTimeout(() => self.retrieveBatchedArtifacts(self), ArtifactApi.ARTIFACT_REQUEST_BATCHINTERVAL);
    }

    addArtifact(projectId: ProjectId, artifactType: ArtifactType, associatedTestRuns: TestRunId[], data: any): Observable<ArtifactId> {
        const artifactData: any = {
          artifactType: artifactType,
          data: data
        };

        if (!!associatedTestRuns && associatedTestRuns.length > 0) {
            artifactData['associatedTestRuns'] = associatedTestRuns;
        }

        return this.http.post<ArtifactId>(`/projects/${projectId}/artifacts`, artifactData);
    }

}
